import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eyebrow, Pill, SectionStamp } from '../components/Atoms';
import DesktopHeader from '../components/DesktopHeader';
import DesktopFooter from '../components/DesktopFooter';
import DesktopWhatsAppFab from '../components/DesktopWhatsAppFab';
import { fonts } from '../theme';

const METHOD = [
  { k: 'Listen', v: 'A 30-minute first consult — history, lifestyle, goals.' },
  { k: 'Assess', v: 'Range, posture, gait, strength. Hands-on, no shortcuts.' },
  { k: 'Plan', v: 'A weekly programme tied to a measurable outcome.' },
  { k: 'Refine', v: 'We revisit and adjust at every session.' },
];

const FACTS = [
  { k: 'License', v: 'GPC-21250' },
  { k: 'Education', v: 'KPGU' },
  { k: 'Practising since', v: '2021' },
  { k: 'Languages', v: 'EN · HI · GU' },
];

export default function AboutDesktop({ t }) {
  const navigate = useNavigate();

  return (
    <div style={{
      width: '100%', minHeight: '100vh', background: t.paper, color: t.ink,
      fontFamily: fonts.sans, position: 'relative', overflow: 'hidden',
    }}>
      <DesktopHeader t={t} />

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 560,
        background: t.paper, color: t.ink, alignItems: 'stretch',
      }}>
        <div style={{
          padding: '80px max(56px, 4vw) 56px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <Eyebrow t={t}>Practitioner · Vadodara</Eyebrow>
          <h1 style={{
            fontFamily: fonts.serif, fontWeight: 300,
            fontSize: 'clamp(56px, 7vw, 96px)', lineHeight: 0.92,
            letterSpacing: '-0.04em', margin: '24px 0 0', color: t.ink,
            textWrap: 'balance',
          }}>
            Dr. Moksha<br/>
            <span style={{ fontStyle: 'italic' }}>Patel,</span>{' '}
            <span style={{
              color: t.mintDeep, fontFamily: fonts.mono, fontSize: '0.32em',
              letterSpacing: '0.04em', verticalAlign: 'middle',
            }}>(PT)</span>
          </h1>
          <p style={{
            fontFamily: fonts.serif, fontSize: 22, fontWeight: 300, lineHeight: 1.4,
            margin: '32px 0 0', maxWidth: 520, color: t.ink,
          }}>
            <span style={{
              fontFamily: fonts.serif, fontSize: 80, fontStyle: 'italic',
              float: 'left', lineHeight: 0.85, marginRight: 12, marginTop: 8, color: t.mintDeep,
            }}>I</span>
            <span> am a dedicated physiotherapist with a passion for helping patients regain mobility, strength, and confidence in their daily lives — through care that listens before it prescribes.</span>
          </p>
        </div>
        <div style={{
          position: 'relative', overflow: 'hidden', minHeight: 480,
          background: t.ink,
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(60% 70% at 50% 30%, ${t.tint}cc, ${t.ink})`,
          }}/>
          <img src={`${process.env.PUBLIC_URL}/assets/hero.webp`} alt="Dr. Moksha Patel" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'contain', objectPosition: 'center bottom',
          }}/>
          <div style={{
            position: 'absolute', left: 32, top: 32, color: t.paper,
            fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
            opacity: 0.7,
          }}>
            Vadodara · Est. 2021
          </div>
          <div style={{
            position: 'absolute', left: 32, bottom: 32, right: 32, color: t.paper,
            fontFamily: fonts.serif, fontSize: 22, fontStyle: 'italic',
          }}>
            &ldquo;Movement is medicine.&rdquo;
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        padding: '40px max(56px, 4vw)',
        borderTop: `0.5px solid ${t.hair}`, borderBottom: `0.5px solid ${t.hair}`,
        maxWidth: 1440, margin: '0 auto',
      }}>
        {FACTS.map((f, i) => (
          <div key={f.k} style={{
            borderLeft: i ? `0.5px solid ${t.hair}` : 'none',
            paddingLeft: i ? 32 : 0,
          }}>
            <div style={{
              fontFamily: fonts.mono, fontSize: 10, color: t.muted,
              letterSpacing: '0.18em', textTransform: 'uppercase',
            }}>{f.k}</div>
            <div style={{ fontFamily: fonts.serif, fontSize: 32, fontWeight: 300, marginTop: 10 }}>
              {f.v}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        padding: '80px max(56px, 4vw)', maxWidth: 1440, margin: '0 auto',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 56, alignItems: 'start' }}>
          <div>
            <SectionStamp n="01" label="Statement" t={t}/>
            <h2 style={{
              fontFamily: fonts.serif, fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 300,
              letterSpacing: '-0.03em', lineHeight: 1.05, margin: '16px 0 0',
              textWrap: 'balance',
            }}>
              Care that listens, <span style={{ fontStyle: 'italic', color: t.mintDeep }}>before</span> it prescribes.
            </h2>
          </div>
          <div>
            <p style={{
              fontFamily: fonts.sans, fontSize: 16, lineHeight: 1.6, color: t.ink, margin: 0,
              maxWidth: 620,
            }}>
              I specialise in orthopaedic, neurological, paediatric, geriatric and post-operative care. Each plan is built around the patient — not the protocol — and revised every session as you progress.
            </p>
          </div>
        </div>
      </div>

      <div style={{
        padding: '0 max(56px, 4vw) 80px', maxWidth: 1440, margin: '0 auto',
      }}>
        <SectionStamp n="02" label="Method" t={t}/>
        <h2 style={{
          fontFamily: fonts.serif, fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 300,
          letterSpacing: '-0.035em', lineHeight: 1.02, margin: '20px 0 40px', maxWidth: 700,
          textWrap: 'balance',
        }}>
          A four-step rhythm, <span style={{ fontStyle: 'italic' }}>repeated</span> every visit.
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {METHOD.map((row, i) => (
            <div key={i} style={{
              padding: 28, borderRadius: 18, border: `0.5px solid ${t.hair}`,
              background: 'transparent', minHeight: 220,
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <div style={{
                fontFamily: fonts.serif, fontSize: 32, fontStyle: 'italic',
                color: t.mintDeep, lineHeight: 1,
              }}>{String(i + 1).padStart(2, '0')}</div>
              <div>
                <div style={{
                  fontFamily: fonts.serif, fontSize: 26, fontWeight: 400, letterSpacing: '-0.02em',
                }}>{row.k}</div>
                <div style={{
                  fontFamily: fonts.sans, fontSize: 13, color: t.muted, marginTop: 8, lineHeight: 1.5,
                }}>{row.v}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40 }}>
          <Pill t={t} fill={t.ink} ink={t.paper} icon="calendar" onClick={() => navigate('/book')}>
            Book a first consult
          </Pill>
        </div>
      </div>

      <DesktopFooter t={t} />
      <DesktopWhatsAppFab />
    </div>
  );
}
